import React, { useState, useEffect, useRef } from "react";
import { useContext } from "react";
import styled, { createGlobalStyle } from "styled-components";
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  Switch,
  Upload,
  Button,
  message,
  Tag,
  Divider,
  Popover,
  Radio,
  Alert,
  Card,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  InfoCircleOutlined,
  HomeOutlined,
  BulbOutlined,
  UploadOutlined,
  PlusOutlined,
  CloseOutlined,
  HolderOutlined,
  LoadingOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMutation } from "@tanstack/react-query";
import { AuthContext } from "../../context/AuthProvider";
import listingModel from "../../lib/firebase/listingModel";
import { updateUnitForSyndication } from "../../lib/api/unitsApi";
import { trackEvent } from "../../helpers/mixpanel";
import { trackProduct } from "../../lib/analytics/trackProduct";
import { ensureWashingtonHighCompliance } from "../../helpers/compliance";
import { db, uploadImage } from "../../helpers/firebase";
import dayjs from "dayjs";
import { collection, doc } from "firebase/firestore";
import { useBreakpoints } from "../../lib/breakpoints";
import AvailabilityScheduler, {
  defaultAvailabilitySchedule,
} from "../vacancy/AvailabilityScheduler";
import pluralize from "pluralize";

// Global styles for Select dropdown (renders in portal)
const TourAvailabilityPopoverStyles = createGlobalStyle`
  .tour-availability-popover {
    .ant-popover-inner {
      padding: 1rem 1.25rem !important;
      border-radius: 12px !important;
      box-shadow: 0 8px 32px rgba(44, 95, 45, 0.15), 0 4px 16px rgba(0, 0, 0, 0.1) !important;
    }
    
    .ant-popover-inner-content {
      font-family: var(--font-secondary) !important;
      font-size: 0.95rem !important;
      line-height: 1.5 !important;
      color: var(--color-text) !important;
      max-width: 300px !important;
    }
    
    /* Ensure popover stays visible on mobile when tapped */
    @media (max-width: 768px) {
      pointer-events: auto !important;
      
      .ant-popover-inner {
        pointer-events: auto !important;
      }
    }
  }
`;

const SelectDropdownStyles = createGlobalStyle`
  .ant-select-dropdown {
    border-radius: 8px !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12) !important;
    border: 1px solid #e2e8f0 !important;
    background: white !important;
    padding: 4px 0 !important;
    
    .rc-virtual-list {
      background: transparent !important;
    }
    
    .ant-select-item {
      border: none !important;
      background: transparent !important;
      padding: 8px 12px !important;
      
      &:hover:not(.ant-select-item-option-disabled) {
        background: #f8fafc !important;
      }
      
      &.ant-select-item-option-selected {
        background: var(--color-primary-glow) !important;
        color: var(--color-primary-darker) !important;
        font-weight: 600;
      }
      
      &.ant-select-item-option-active:not(.ant-select-item-option-disabled) {
        background: #f1f5f9 !important;
      }
    }
  }
`;

/** Ensure create/edit listing is vertically centered (Ant default top offset + legacy CSS otherwise pin it high). */
const CreateListingModalWrapStyles = createGlobalStyle`
  .lo-create-listing-modal-wrap.ant-modal-wrap {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    box-sizing: border-box !important;
    padding: 20px 16px !important;
    overflow-y: auto !important;
  }

  .lo-create-listing-modal-wrap .ant-modal {
    top: 0 !important;
    margin: 0 auto !important;
    padding-bottom: 0 !important;
  }
`;

const { TextArea } = Input;
const { Dragger } = Upload;

/**
 * CreateListingModal - Modal for creating or editing a listing
 *
 * Features:
 * - Select from available units (no existing listings) - only when creating
 * - Pre-populate from unit data or existing listing data
 * - Customize listing details
 * - Upload additional photos
 * - Set requirements (credit score, income, pets)
 * - One listing per unit constraint enforced (only when creating)
 */
const CreateListingModal = ({
  isOpen,
  onClose,
  availableUnits = [],
  allUnits = [],
  editingListing = null,
  onSuccess,
  primaryAccountId = null,
  properties = [],
}) => {
  const { user } = useContext(AuthContext);
  const { isMobile } = useBreakpoints();
  const [form] = Form.useForm();
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [uploadedPhotoUrls, setUploadedPhotoUrls] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [draftListingId, setDraftListingId] = useState(null);
  const [firstNewUploadedPhotoUrl, setFirstNewUploadedPhotoUrl] =
    useState(null);
  const [activePhotoUid, setActivePhotoUid] = useState(null);
  const isEditing = !!editingListing;

  // Drag-sort (dnd-kit): prevent the "click" that often fires right after drag on touch devices.
  const suppressNextTileClickRef = useRef(false);
  // Parent passes `availableUnits` / `allUnits` as freshly filtered arrays each render; they must not
  // be effect deps or opening the create modal causes resetFields + setFieldsValue every render → infinite loop.
  const availableUnitsRef = useRef(availableUnits);
  const allUnitsRef = useRef(allUnits);
  availableUnitsRef.current = availableUnits;
  allUnitsRef.current = allUnits;

  const dndSensors = useSensors(
    // Mouse + Touch sensors are the most reliable combo across browsers (vs PointerSensor quirks).
    useSensor(MouseSensor, { activationConstraint: { distance: 1 } }),
    // Handle-only dragging: start immediately on touch; we don't need long-press.
    useSensor(TouchSensor, { activationConstraint: { delay: 0, tolerance: 0 } })
  );

  // Mobile: tap anywhere outside the thumbnail/actions to dismiss the overlay
  useEffect(() => {
    if (!isOpen || !isMobile || !activePhotoUid) return;

    const onPointerDown = (e) => {
      const target = e.target;
      if (!target || typeof target.closest !== "function") return;

      // If the user is interacting with an action button (eye/X), don't dismiss.
      if (target.closest(".ant-upload-list-item-actions")) return;

      // If the user tapped any thumbnail tile, that tile handler will manage active state.
      if (target.closest(".photo-dnd-item")) return;

      setActivePhotoUid(null);
    };

    document.addEventListener("pointerdown", onPointerDown, { capture: true });
    return () =>
      document.removeEventListener("pointerdown", onPointerDown, {
        capture: true,
      });
  }, [isOpen, isMobile, activePhotoUid]);

  // Track modal open
  useEffect(() => {
    if (isOpen) {
      trackEvent("listing_modal_opened", {
        is_editing: isEditing,
        listing_id: editingListing?.id,
        landlord_id: user?.uid,
        available_units_count: availableUnits.length,
      });
    }
  }, [isOpen, isEditing, editingListing?.id, user?.uid, availableUnits.length]);

  // Populate form when editing
  useEffect(() => {
    if (isEditing && editingListing && isOpen) {
      // Find the unit for this listing (unit has a listing so it's in allUnits, not availableUnits)
      const au = availableUnitsRef.current;
      const allU = allUnitsRef.current;
      const unitsToSearch = allU.length > 0 ? allU : au;
      const unit = unitsToSearch.find((u) => u.id === editingListing.unitId);
      if (unit) {
        setSelectedUnit(unit);
      }
      // Prefer syndicateToZillow from listing (we store it on save); fallback to unit's marketing flags
      const syndicated =
        editingListing.syndicateToZillow === true ||
        (unit &&
          (unit.marketing_enabled === true ||
            (unit.marketing_enabled === undefined &&
              unit.marketing_active === true)));

      // Populate form with existing listing data
      form.setFieldsValue({
        unitId: editingListing.unitId,
        syndicateToZillow: !!syndicated,
        headline: editingListing.headline || editingListing.title,
        description: editingListing.description,
        listingType: editingListing.listingType || "rent",
        rentAmount: editingListing.rentAmount,
        deposit: editingListing.securityDeposit || editingListing.deposit,
        additional_mandatory_fees: editingListing.additional_mandatory_fees
          ?.length
          ? editingListing.additional_mandatory_fees
          : [],
        bedrooms: editingListing.bedrooms,
        bathrooms: editingListing.bathrooms,
        sqft: editingListing.sqft,
        availableDate: editingListing.availableDate
          ? dayjs(editingListing.availableDate)
          : dayjs(),
        petsAllowed:
          editingListing.rules?.petsAllowed ??
          editingListing.petsAllowed ??
          false,
        minCreditScore:
          editingListing.rules?.minCreditScore ??
          editingListing.minCreditScore ??
          650,
        minIncomeMultiple:
          editingListing.rules?.minIncomeMultiple ??
          editingListing.minIncomeMultiple ??
          2.5,
        // Additional screening questions
        askSmoking: editingListing.rules?.askSmoking ?? false,
        askOccupants: editingListing.rules?.askOccupants ?? false,
        askGuarantor: editingListing.rules?.askGuarantor ?? false,
        askLawsuit: editingListing.rules?.askLawsuit ?? false,
        askFelony: editingListing.rules?.askFelony ?? false,
        availability:
          editingListing.availability ??
          editingListing.publicMetadata?.availability ??
          defaultAvailabilitySchedule(),
        amenities: editingListing.amenities || [],
        address: editingListing.address || "",
        city: editingListing.city || "",
        state: editingListing.state || "",
        zipCode: editingListing.zipCode || "",
        leoPrivateInfo:
          editingListing.leoPrivateInfo ||
          editingListing.privateInfoForLeo ||
          "",
        // Rent-to-own fields
        rentToOwn: editingListing.rentToOwn
          ? {
              downPayment: editingListing.rentToOwn.downPayment,
              monthlyPayment: editingListing.rentToOwn.monthlyPayment,
              term: editingListing.rentToOwn.term,
              interestRate: editingListing.rentToOwn.interestRate,
              creditCheckRequired:
                editingListing.rentToOwn.creditCheckRequired ?? true,
              incomeBasedQualification:
                editingListing.rentToOwn.incomeBasedQualification ?? true,
              taxesInsuranceIncluded:
                editingListing.rentToOwn.taxesInsuranceIncluded ?? false,
              moveInDate: editingListing.rentToOwn.moveInDate
                ? dayjs(editingListing.rentToOwn.moveInDate)
                : null,
              toursAvailable: editingListing.rentToOwn.toursAvailable ?? false,
              financingDescription:
                editingListing.rentToOwn.financingDescription || "",
            }
          : undefined,
      });

      // Set existing photos
      if (editingListing.photos && editingListing.photos.length > 0) {
        setUploadedPhotoUrls(editingListing.photos);
        setFileList(
          editingListing.photos.map((url, index) => ({
            uid: `-${index}`,
            name: `photo-${index}.jpg`,
            status: "done",
            url: url,
          }))
        );
      }
      // Reset upload-session meta
      setFirstNewUploadedPhotoUrl(null);
    } else if (!isEditing && isOpen) {
      // Reset form when creating
      form.resetFields();
      // Set sensible defaults for new listings
      form.setFieldsValue({
        listingType: "rent",
        minCreditScore: 650,
        minIncomeMultiple: 2.5,
        availability: defaultAvailabilitySchedule(),
        additional_mandatory_fees: [],
        // Additional screening questions (default to false)
        askSmoking: false,
        askOccupants: false,
        askGuarantor: false,
        askLawsuit: false,
        askFelony: false,
      });
      setSelectedUnit(null);
      setFileList([]);
      setUploadedPhotoUrls([]);
      // Generate a stable draft listing id for uploads before submit
      setDraftListingId(doc(collection(db, "listings")).id);
      setFirstNewUploadedPhotoUrl(null);
      setActivePhotoUid(null);
    }
  }, [isEditing, editingListing, isOpen, form]);

  // Create or update listing mutation
  const createMutation = useMutation({
    mutationFn: (listingData) => {
      if (isEditing) {
        return listingModel.updateListing(editingListing.id, listingData);
      } else {
        return listingModel.createListing(listingData, draftListingId);
      }
    },
    onSuccess: (result, variables) => {
      if (isEditing) {
        message.success("Listing updated successfully!");
        // Get form values for tracking since result might not have full data
        const formValues = form.getFieldsValue();
        trackProduct("listing_updated", {
          listing_id: editingListing?.id,
          landlord_id: user.uid,
          rent_amount: formValues.rentAmount || editingListing?.rentAmount,
          bedrooms: formValues.bedrooms || editingListing?.bedrooms,
          bathrooms: formValues.bathrooms || editingListing?.bathrooms,
          photos_count: uploadedPhotoUrls.length,
          has_amenities:
            (formValues.amenities?.length ||
              editingListing?.amenities?.length ||
              0) > 0,
        });
      } else {
        message.success("Listing created successfully!");
        // For create, result should have the full listing data
        trackProduct("listing_created", {
          listing_id: result?.id,
          unit_id: result?.unitId,
          landlord_id: user.uid,
          rent_amount: result?.rentAmount,
          bedrooms: result?.bedrooms,
          bathrooms: result?.bathrooms,
          photos_count: uploadedPhotoUrls.length,
          has_amenities: (result?.amenities?.length || 0) > 0,
        });
      }

      // When "Syndicate to Zillow / HotPads" is on, set unit marketing_enabled and status for feed
      const unitId = result?.unitId || variables?.unitId;
      if (unitId) {
        const syndicate = variables?.syndicateToZillow === true;
        updateUnitForSyndication(unitId, {
          marketing_enabled: syndicate,
          marketing_active: syndicate,
          ...(syndicate && { status: "VACANT" }),
        }).catch(() => {
          message.warning(
            "Listing saved. Syndication update may need to be set on the unit separately."
          );
        });
      }

      form.resetFields();
      setSelectedUnit(null);
      setFileList([]);
      setUploadedPhotoUrls([]);
      onSuccess?.();
    },
    onError: (error) => {
      // Make error message more human-readable
      let errorMessage = "Failed to create listing. ";

      if (
        error.message?.includes("undefined") ||
        error.message?.includes("Unsupported field value")
      ) {
        // Check which field is undefined
        if (error.message?.includes("sqft")) {
          errorMessage +=
            "Please enter a value for Square Feet, or leave it blank if not applicable.";
        } else if (error.message?.includes("bedrooms")) {
          errorMessage += "Please enter the number of bedrooms.";
        } else if (error.message?.includes("bathrooms")) {
          errorMessage += "Please enter the number of bathrooms.";
        } else {
          errorMessage +=
            "Please make sure all required fields are filled in correctly.";
        }
      } else if (error.message?.includes("already has an active listing")) {
        errorMessage = error.message;
      } else {
        errorMessage += error.message || "Please try again.";
      }

      message.error(errorMessage);
    },
  });

  // Handle unit selection
  const handleUnitSelect = (unitId) => {
    const unit = availableUnits.find((u) => u.id === unitId);
    setSelectedUnit(unit);

    if (unit) {
      // Prefer discrete address fields (address_street, address_city, address_state, address_zip)
      let address = "";
      let city = "";
      let state = "";
      let zipCode = "";
      if (
        unit.address_street ||
        unit.address_city ||
        unit.address_state ||
        unit.address_zip
      ) {
        address = unit.address_street || "";
        city = unit.address_city || "";
        state = unit.address_state || "";
        zipCode = unit.address_zip || "";
      } else if (typeof unit.address === "string") {
        address = unit.address;
        city = unit.city || "";
        state = unit.state || "";
        zipCode = unit.zip || unit.postalCode || unit.zipCode || "";
      } else if (unit.address && typeof unit.address === "object") {
        address = unit.address.street || unit.address.address || "";
        city = unit.address.city || unit.city || "";
        state = unit.address.state || unit.state || "";
        zipCode =
          unit.address.zipCode ||
          unit.address.zip ||
          unit.zip ||
          unit.postalCode ||
          unit.zipCode ||
          "";
      } else {
        address = unit.addressLine1 || unit.street || unit.address || "";
        city = unit.city || "";
        state = unit.state || "";
        zipCode = unit.zip || unit.postalCode || unit.zipCode || "";
      }

      // Format full address for headline
      const fullAddress =
        [address, city, state, zipCode].filter(Boolean).join(", ") || address;

      // Pre-populate form with unit data
      form.setFieldsValue({
        unitId: unit.id,
        address: address,
        city: city,
        state: state,
        zipCode: zipCode,
        headline: `${unit.bedrooms} Bed, ${unit.bathrooms} Bath in ${fullAddress}`,
        description:
          unit.description ||
          `Beautiful ${unit.bedrooms} bedroom, ${unit.bathrooms} bathroom unit available for rent.`,
        rentAmount: unit.currentRent || unit.baseRent,
        deposit:
          unit.securityDeposit || (unit.currentRent || unit.baseRent) * 1.5,
        additional_mandatory_fees: unit.additional_mandatory_fees?.length
          ? unit.additional_mandatory_fees
          : [],
        bedrooms: unit.bedrooms,
        bathrooms: unit.bathrooms,
        sqft: unit.sqft,
        availableDate: unit.availableDate ? dayjs(unit.availableDate) : dayjs(),
        petsAllowed: unit.petsAllowed || false,
        minCreditScore:
          unit.minCreditScore !== undefined ? unit.minCreditScore : 650,
        minIncomeMultiple:
          unit.minIncomeMultiple !== undefined ? unit.minIncomeMultiple : 2.5,
        // Additional screening questions (default to false)
        askSmoking: false,
        askOccupants: false,
        askGuarantor: false,
        askLawsuit: false,
        askFelony: false,
        availability: unit.availability || defaultAvailabilitySchedule(),
        amenities: unit.amenities || [],
      });
    }
  };

  const handleSubmit = async (values) => {
    try {
      // Deposit account is only required when turning the listing Live (enforced on the status toggle in MarketingListings).
      // Saving/updating listing details uses the primary account when no property/unit override is set.
      const dedupe = (arr) => Array.from(new Set((arr || []).filter(Boolean)));

      // Ensure address fields are extracted from form values or unit/listing as fallback
      const address =
        values.address ||
        selectedUnit?.address ||
        editingListing?.address ||
        "";
      const city =
        values.city || selectedUnit?.city || editingListing?.city || "";
      const state =
        values.state || selectedUnit?.state || editingListing?.state || "";
      const zipCode =
        values.zipCode ||
        selectedUnit?.zip ||
        selectedUnit?.zipCode ||
        selectedUnit?.postalCode ||
        editingListing?.zipCode ||
        "";

      // Photos + meta image selection:
      // - Creating: first *uploaded* photo wins, then unit photos (if any)
      // - Editing: if user uploaded any new photos in this edit session, the first new upload wins
      let photosForListing = [];
      let metaImageUrl = null;

      if (isEditing) {
        const all = dedupe(uploadedPhotoUrls);
        if (firstNewUploadedPhotoUrl) {
          photosForListing = dedupe([
            firstNewUploadedPhotoUrl,
            ...all.filter((u) => u !== firstNewUploadedPhotoUrl),
          ]);
          metaImageUrl = firstNewUploadedPhotoUrl;
        } else {
          photosForListing = all;
          metaImageUrl = all[0] || null;
        }
      } else {
        const uploaded = dedupe(uploadedPhotoUrls);
        const unitPhotos = dedupe(selectedUnit?.photos || []);
        photosForListing = dedupe([...uploaded, ...unitPhotos]);
        metaImageUrl = uploaded[0] || unitPhotos[0] || null;
      }

      // Prepare rent-to-own data if applicable
      let rentToOwnData = null;
      if (values.listingType === "rent_to_own" && values.rentToOwn) {
        rentToOwnData = {
          downPayment: values.rentToOwn.downPayment,
          monthlyPayment: values.rentToOwn.monthlyPayment,
          term: values.rentToOwn.term,
          interestRate: values.rentToOwn.interestRate,
          creditCheckRequired: values.rentToOwn.creditCheckRequired ?? true,
          incomeBasedQualification:
            values.rentToOwn.incomeBasedQualification ?? true,
          taxesInsuranceIncluded:
            values.rentToOwn.taxesInsuranceIncluded ?? false,
          moveInDate: values.rentToOwn.moveInDate
            ? values.rentToOwn.moveInDate.toISOString()
            : null,
          toursAvailable: values.rentToOwn.toursAvailable ?? false,
          financingDescription: values.rentToOwn.financingDescription || "",
        };
        // Set rentAmount to monthlyPayment for rent-to-own (for backward compatibility and display)
        values.rentAmount = values.rentToOwn.monthlyPayment;
      }

      const listingData = {
        ...values,
        listingType: values.listingType || "rent",
        // Ensure address fields are saved separately for Google Maps and display
        address: address,
        city: city,
        state: state,
        zipCode: zipCode,
        // Only include sqft if it has a value (Firestore doesn't allow undefined)
        sqft:
          values.sqft !== undefined && values.sqft !== null
            ? values.sqft
            : null,
        bedrooms:
          values.bedrooms !== undefined && values.bedrooms !== null
            ? values.bedrooms
            : null,
        bathrooms:
          values.bathrooms !== undefined && values.bathrooms !== null
            ? values.bathrooms
            : null,
        availableDate: values.availableDate
          ? values.availableDate.toISOString()
          : new Date().toISOString(),
        photos: photosForListing,
        // Used for OG/Twitter cards and integrations that want a canonical cover image
        metaImageUrl: metaImageUrl,
        // Rent-to-own data (only include if listing type is rent_to_own)
        rentToOwn: rentToOwnData,
        // Non-PII listing metadata for external consumers
        publicMetadata: {
          headline: values.headline || "",
          description: values.description || "",
          rentAmount: values.rentAmount ?? null,
          deposit: values.deposit ?? null,
          bedrooms:
            values.bedrooms !== undefined && values.bedrooms !== null
              ? values.bedrooms
              : null,
          bathrooms:
            values.bathrooms !== undefined && values.bathrooms !== null
              ? values.bathrooms
              : null,
          sqft:
            values.sqft !== undefined && values.sqft !== null
              ? values.sqft
              : null,
          availableDate: values.availableDate
            ? values.availableDate.toISOString()
            : new Date().toISOString(),
          address,
          city,
          state,
          zipCode,
          amenities: values.amenities || [],
          rules: {
            petsAllowed: values.petsAllowed,
            minCreditScore: values.minCreditScore,
            minIncomeMultiple: values.minIncomeMultiple,
            // Additional screening questions
            askSmoking: values.askSmoking ?? false,
            askOccupants: values.askOccupants ?? false,
            askGuarantor: values.askGuarantor ?? false,
            askLawsuit: values.askLawsuit ?? false,
            askFelony: values.askFelony ?? false,
          },
          availability: values.availability || null,
          metaImageUrl,
          photosCount: photosForListing.length,
        },
        rules: {
          petsAllowed: values.petsAllowed,
          minCreditScore: values.minCreditScore,
          minIncomeMultiple: values.minIncomeMultiple,
          // Additional screening questions
          askSmoking: values.askSmoking ?? false,
          askOccupants: values.askOccupants ?? false,
          askGuarantor: values.askGuarantor ?? false,
          askLawsuit: values.askLawsuit ?? false,
          askFelony: values.askFelony ?? false,
        },
        availability: values.availability || null,
        leoPrivateInfo: values.leoPrivateInfo || "",
        syndicateToZillow: !!values.syndicateToZillow,
        additional_mandatory_fees: Array.isArray(
          values.additional_mandatory_fees
        )
          ? values.additional_mandatory_fees
              .filter((f) => f?.name && Number(f?.amount) >= 0)
              .map((f) => ({
                name: String(f.name).trim(),
                amount: Number(f.amount) || 0,
                frequency: f.frequency || "monthly",
              }))
          : [],
      };

      // Keep door/property links current so lease-up property filters resolve by address.
      if (selectedUnit?.id) {
        listingData.unitId = selectedUnit.id;
        if (selectedUnit.propertyId) {
          listingData.propertyId = selectedUnit.propertyId;
        }
      }

      // Only set these fields when creating (not when editing)
      if (!isEditing) {
        listingData.landlordId = user.uid;
        listingData.landlordEmail = user.email;
        listingData.status = "active";
        listingData.viewCount = 0;
        listingData.inquiryCount = 0;
      }

      // Remove undefined values to prevent Firestore errors
      Object.keys(listingData).forEach((key) => {
        if (listingData[key] === undefined) {
          delete listingData[key];
        }
      });

      await createMutation.mutateAsync(listingData);
      if (user?.uid) {
        await ensureWashingtonHighCompliance(user.uid, address, state);
      }
    } catch (error) {
      console.error("Error creating listing:", error);

      // Make error message more human-readable
      let errorMessage = "Failed to create listing. ";

      if (
        error.message?.includes("undefined") ||
        error.message?.includes("Unsupported field value")
      ) {
        // Check which field is undefined
        if (error.message?.includes("sqft")) {
          errorMessage +=
            "Please enter a value for Square Feet, or leave it blank if not applicable.";
        } else if (error.message?.includes("bedrooms")) {
          errorMessage += "Please enter the number of bedrooms.";
        } else if (error.message?.includes("bathrooms")) {
          errorMessage += "Please enter the number of bathrooms.";
        } else {
          errorMessage +=
            "Please make sure all required fields are filled in correctly.";
        }
      } else if (error.message?.includes("already has an active listing")) {
        errorMessage = error.message;
      } else {
        errorMessage += error.message || "Please try again.";
      }

      message.error(errorMessage);
    }
  };

  const handleCancel = () => {
    trackEvent("listing_modal_closed", {
      is_editing: isEditing,
      listing_id: editingListing?.id,
      landlord_id: user?.uid,
      method: "cancel",
      has_changes: form.isFieldsTouched(),
    });
    form.resetFields();
    setSelectedUnit(null);
    setFileList([]);
    setUploadedPhotoUrls([]);
    onClose();
  };

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error(`${file.name} is not an image file`);
      return false;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error("Image must be smaller than 5MB");
      return false;
    }
    return true;
  };

  const handleImageUpload = async ({ file, onSuccess, onError }) => {
    setIsUploading(true);

    // Optimistic UI: show an "uploading" tile immediately so users see progress.
    setFileList((prev) => {
      if (prev.some((item) => item.uid === file.uid)) return prev;
      return [
        ...prev,
        {
          uid: file.uid,
          name: file.name,
          status: "uploading",
          percent: 0,
        },
      ];
    });

    trackEvent("listing_photo_upload_started", {
      is_editing: isEditing,
      listing_id: editingListing?.id,
      landlord_id: user?.uid,
      file_name: file.name,
      file_size: file.size,
      current_photo_count: uploadedPhotoUrls.length,
    });

    try {
      const targetListingId = isEditing ? editingListing?.id : draftListingId;
      if (!targetListingId) {
        throw new Error(
          "Missing listing id for photo upload. Please close and reopen the modal and try again."
        );
      }

      const imageUrl = await uploadImage(
        `listing-photos/${targetListingId}`,
        file
      );
      // Track the first newly-uploaded photo this session so it can become the meta/cover image.
      if (!firstNewUploadedPhotoUrl) {
        setFirstNewUploadedPhotoUrl(imageUrl);
      }
      setUploadedPhotoUrls((prev) => [...prev, imageUrl]);
      // Update (or insert) the fileList item to show the uploaded file
      setFileList((prev) => {
        const exists = prev.some((item) => item.uid === file.uid);
        if (exists) {
          return prev.map((item) =>
            item.uid === file.uid
              ? {
                  ...item,
                  name: file.name,
                  status: "done",
                  url: imageUrl,
                  response: { url: imageUrl },
                  percent: 100,
                }
              : item
          );
        }
        return [
          ...prev,
          {
            uid: file.uid,
            name: file.name,
            status: "done",
            url: imageUrl,
            response: { url: imageUrl },
            percent: 100,
          },
        ];
      });
      onSuccess(imageUrl, file);
      message.success("Image uploaded");

      trackEvent("listing_photo_uploaded", {
        is_editing: isEditing,
        listing_id: editingListing?.id,
        landlord_id: user?.uid,
        file_name: file.name,
        total_photos: uploadedPhotoUrls.length + 1,
      });
    } catch (error) {
      console.error("Error uploading image:", error);
      message.error("Failed to upload image");

      // Mark the optimistic tile as error so user sees what happened.
      setFileList((prev) =>
        prev.map((item) =>
          item.uid === file.uid
            ? {
                ...item,
                status: "error",
                error: error?.message || "Upload failed",
              }
            : item
        )
      );

      trackEvent("listing_photo_upload_failed", {
        is_editing: isEditing,
        listing_id: editingListing?.id,
        landlord_id: user?.uid,
        file_name: file.name,
        error: error.message,
      });
      onError(error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageRemove = (file) => {
    const urlToRemove = file.url || file.response?.url;

    trackEvent("listing_photo_removed", {
      is_editing: isEditing,
      listing_id: editingListing?.id,
      landlord_id: user?.uid,
      file_name: file.name,
      remaining_photos: uploadedPhotoUrls.length - 1,
    });

    if (urlToRemove) {
      setUploadedPhotoUrls((prev) => prev.filter((url) => url !== urlToRemove));
    }
    setFileList((prev) => prev.filter((item) => item.uid !== file.uid));
    setActivePhotoUid((prev) => (prev === file.uid ? null : prev));
    return true;
  };

  const seePhotoPreview = (file) => {
    // Preview should open via the eye icon. On mobile, taps on the thumbnail itself are intercepted
    // (to show the overlay), so this will primarily fire from the eye button.
    const urlToPreview = file.url || file.response?.url;
    if (!urlToPreview) return;
    window.open(urlToPreview, "_blank", "noopener,noreferrer");
  };

  const reorderPhotos = (fromUid, toUid) => {
    if (!fromUid || !toUid || fromUid === toUid) return;
    setFileList((prev) => {
      const fromIndex = prev.findIndex((f) => f.uid === fromUid);
      const toIndex = prev.findIndex((f) => f.uid === toUid);
      if (fromIndex === -1 || toIndex === -1) return prev;
      const next = arrayMove(prev, fromIndex, toIndex);
      // Keep uploadedPhotoUrls in sync with visual order
      const nextUrls = next
        .map((f) => f.url || f.response?.url)
        .filter(Boolean);
      setUploadedPhotoUrls(nextUrls);
      return next;
    });
  };

  const SortablePhotoTile = ({ id, file, children }) => {
    const isUploadingItem = file?.status === "uploading";
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id,
      disabled: isUploadingItem,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`photo-dnd-item${isDragging ? " is-dnd-dragging" : ""}${
          activePhotoUid === id ? " is-active" : ""
        }`}
        data-photo-uid={id}
        onClickCapture={(e) => {
          if (!isMobile) return;
          if (suppressNextTileClickRef.current) {
            e.preventDefault();
            e.stopPropagation();
            suppressNextTileClickRef.current = false;
            return;
          }
          if (e.target?.closest?.(".ant-upload-list-item-actions")) return;
          if (e.target?.closest?.(".photo-drag-handle")) return;
          e.preventDefault();
          e.stopPropagation();
          setActivePhotoUid(id);
        }}
      >
        {!isUploadingItem && (
          <div
            className="photo-drag-handle"
            role="button"
            aria-label="Drag to reorder"
            {...attributes}
            {...listeners}
            onPointerDownCapture={(e) => {
              // Prevent handle press from toggling overlay
              e.stopPropagation();
            }}
          >
            <HolderOutlined />
          </div>
        )}
        {children}
      </div>
    );
  };

  const handleFinishFailed = (errorInfo) => {
    // Track validation failure
    const errorFields = errorInfo?.errorFields || [];
    trackEvent("listing_form_validation_failed", {
      is_editing: isEditing,
      listing_id: editingListing?.id,
      landlord_id: user?.uid,
      error_fields: errorFields.map((f) => f.name?.[0]).filter(Boolean),
      error_count: errorFields.length,
    });

    // Scroll to the first error field
    if (errorFields.length > 0) {
      const firstErrorField = errorFields[0];
      const fieldName = firstErrorField.name?.[0];

      if (fieldName) {
        // Use setTimeout to ensure the error message is rendered first
        setTimeout(() => {
          form.scrollToField(fieldName, {
            behavior: "smooth",
            block: "center",
          });
        }, 100);
      }
    }
  };

  return (
    <>
      <SelectDropdownStyles />
      <TourAvailabilityPopoverStyles />
      <CreateListingModalWrapStyles />
      <StyledModal
        title={
          <ModalTitle>
            {isEditing ? "Edit Listing" : "Create New Listing"}
          </ModalTitle>
        }
        open={isOpen}
        onCancel={handleCancel}
        footer={null}
        width={700}
        destroyOnClose
        centered
        wrapClassName="lo-create-listing-modal-wrap"
      >
        <StyledForm
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onFinishFailed={handleFinishFailed}
          requiredMark
        >
          {/* Unit Selection - Only show when creating, not editing */}
          {!isEditing && (
            <>
              <SectionWrapper>
                <StepHeader>
                  <StepNumber>1</StepNumber>
                  <StepTitle>Select Property</StepTitle>
                </StepHeader>

                <Form.Item
                  name="unitId"
                  label="Which property do you want to list?"
                  rules={[
                    { required: true, message: "Please select a property" },
                  ]}
                >
                  <Select
                    size="large"
                    placeholder="Select a property"
                    onChange={handleUnitSelect}
                    options={availableUnits.map((unit) => {
                      const addr =
                        unit.address_street ||
                        unit.address_city ||
                        unit.address_state ||
                        unit.address_zip
                          ? [
                              unit.address_street,
                              unit.address_city,
                              unit.address_state,
                              unit.address_zip,
                            ]
                              .filter(Boolean)
                              .join(", ")
                          : unit.address;
                      return {
                        value: unit.id,
                        label: `${addr || "Address not specified"} - ${
                          unit.bedrooms
                        } bed, ${unit.bathrooms} bath`,
                      };
                    })}
                  />
                </Form.Item>
              </SectionWrapper>

              {availableUnits.length === 0 && (
                <EmptyUnitsAlert>
                  <InfoCircleOutlined />
                  <span>
                    All your properties already have listings. Delete an
                    existing listing or add a new property to create more.
                  </span>
                </EmptyUnitsAlert>
              )}

              {!selectedUnit && availableUnits.length > 0 && (
                <EmptyStateContainer>
                  <EmptyStateIcon>
                    <HomeOutlined />
                  </EmptyStateIcon>
                  <EmptyStateTitle>
                    Ready to List Your Property?
                  </EmptyStateTitle>
                  <EmptyStateDescription>
                    Select a property above to get started. We'll pre-fill the
                    listing details with your property information, and you can
                    customize everything before publishing.
                  </EmptyStateDescription>
                  <StepsPreview>
                    <StepPreviewItem>
                      <StepPreviewNumber>2</StepPreviewNumber>
                      <StepPreviewText>
                        Customize listing details
                      </StepPreviewText>
                    </StepPreviewItem>
                    <StepPreviewItem>
                      <StepPreviewNumber>3</StepPreviewNumber>
                      <StepPreviewText>Set tenant requirements</StepPreviewText>
                    </StepPreviewItem>
                    <StepPreviewItem>
                      <StepPreviewNumber>✓</StepPreviewNumber>
                      <StepPreviewText>
                        Publish and start receiving inquiries
                      </StepPreviewText>
                    </StepPreviewItem>
                  </StepsPreview>
                </EmptyStateContainer>
              )}
            </>
          )}

          {(selectedUnit || isEditing) && (
            <>
              {!isEditing && <SectionDivider />}

              {/* Basic Info */}
              <SectionWrapper>
                <StepHeader>
                  <StepNumber>{isEditing ? "1" : "2"}</StepNumber>
                  <StepTitle>Listing Details</StepTitle>
                </StepHeader>

                <Form.Item
                  name="headline"
                  label="Listing Headline"
                  rules={[
                    { required: true, message: "Please enter a headline" },
                  ]}
                  tooltip="Create a compelling headline that highlights key features. This appears first in search results and on your listing page."
                  extra={
                    <Form.Item
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.headline !== currentValues.headline
                      }
                      noStyle
                    >
                      {({ getFieldValue }) => (
                        <CharacterCounter>
                          {getFieldValue("headline")?.length || 0} / 100
                        </CharacterCounter>
                      )}
                    </Form.Item>
                  }
                >
                  <Input
                    size="large"
                    placeholder="e.g. Spacious 2 Bed with Parking Near Downtown"
                    maxLength={100}
                  />
                </Form.Item>

                {/* Address fields - Hidden but saved to ensure they're part of form structure */}
                <Form.Item name="address" hidden>
                  <Input />
                </Form.Item>
                <Form.Item name="city" hidden>
                  <Input />
                </Form.Item>
                <Form.Item name="state" hidden>
                  <Input />
                </Form.Item>
                <Form.Item name="zipCode" hidden>
                  <Input />
                </Form.Item>

                <Form.Item
                  name="description"
                  label="Description"
                  rules={[
                    { required: true, message: "Please enter a description" },
                  ]}
                  tooltip="Describe what makes this property special"
                  extra={
                    <Form.Item
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.description !== currentValues.description
                      }
                      noStyle
                    >
                      {({ getFieldValue }) => (
                        <CharacterCounter>
                          {getFieldValue("description")?.length || 0} / 1000
                        </CharacterCounter>
                      )}
                    </Form.Item>
                  }
                >
                  <TextArea
                    rows={4}
                    placeholder="This beautiful property features modern appliances, spacious closets, and natural light throughout..."
                    maxLength={1000}
                  />
                </Form.Item>

                {/* Syndication Checklist: Zillow compliance */}
                <Card
                  size="small"
                  title="Syndication Checklist"
                  style={{ marginBottom: 16 }}
                  headStyle={{
                    background: "var(--gradient-primary) !important",
                    color: "white",
                    borderBottom: "none",
                    fontWeight: 600,
                  }}
                >
                  <Form.Item
                    name="syndicateToZillow"
                    label="Syndicate to Zillow / HotPads"
                    valuePropName="checked"
                    initialValue={false}
                    style={{ marginBottom: 12 }}
                    extra={
                      <Typography.Text
                        type="secondary"
                        style={{
                          fontSize: 13,
                          lineHeight: 1.5,
                          display: "block",
                          marginTop: 4,
                        }}
                      >
                        When this is on, your listing is included in the feed we send to Zillow
                        and HotPads. It usually goes live on those sites shortly after you save,
                        but the exact timing depends on when Zillow next pulls feed updates. We
                        don&apos;t control that schedule.
                      </Typography.Text>
                    }
                  >
                    <Switch />
                  </Form.Item>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prev, cur) =>
                      prev.sqft !== cur.sqft ||
                      prev.syndicateToZillow !== cur.syndicateToZillow
                    }
                  >
                    {({ getFieldValue }) => {
                      const sqft = getFieldValue("sqft");
                      const hasSqft =
                        sqft !== undefined && sqft !== null && sqft !== "";
                      const hasPhotos = uploadedPhotoUrls.length > 0;
                      const showWarnings = getFieldValue("syndicateToZillow");
                      return (
                        <>
                          {showWarnings && (
                            <>
                              {!hasPhotos && (
                                <Alert
                                  type="warning"
                                  showIcon
                                  message="No exterior photo detected (Zillow requirement)."
                                  description="Add at least one high-res photo. The first image is used as the hero shot."
                                  style={{ marginBottom: 8 }}
                                />
                              )}
                              {!hasSqft && (
                                <Alert
                                  type="warning"
                                  showIcon
                                  message="Missing square footage."
                                  description="Zillow recommends including square footage for better listing quality."
                                  style={{ marginBottom: 8 }}
                                />
                              )}
                            </>
                          )}
                        </>
                      );
                    }}
                  </Form.Item>
                </Card>

                {/* Listing Type */}
                <Form.Item
                  name="listingType"
                  label="Listing Type"
                  rules={[
                    { required: true, message: "Please select listing type" },
                  ]}
                  initialValue="rent"
                >
                  <StyledRadioGroup size="large">
                    <Radio.Button value="rent">For Rent</Radio.Button>
                    <Radio.Button value="rent_to_own">Rent-to-Own</Radio.Button>
                  </StyledRadioGroup>
                </Form.Item>

                <Form.Item
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.listingType !== currentValues.listingType
                  }
                  noStyle
                >
                  {({ getFieldValue }) => {
                    const listingType = getFieldValue("listingType");
                    const isRentToOwn = listingType === "rent_to_own";

                    return (
                      <>
                        {!isRentToOwn && (
                          <>
                            <FormRow className="rent-deposit-row">
                              <Form.Item
                                name="rentAmount"
                                label="Monthly Rent"
                                rules={[
                                  { required: true, message: "Required" },
                                ]}
                              >
                                <InputNumber
                                  size="large"
                                  prefix="$"
                                  style={{ width: "100%" }}
                                  min={0}
                                  formatter={(value) =>
                                    `${value}`.replace(
                                      /\B(?=(\d{3})+(?!\d))/g,
                                      ","
                                    )
                                  }
                                  parser={(value) =>
                                    value.replace(/\$\s?|(,*)/g, "")
                                  }
                                />
                              </Form.Item>

                              <Form.Item
                                name="deposit"
                                label="Security Deposit"
                                rules={[
                                  { required: true, message: "Required" },
                                ]}
                              >
                                <InputNumber
                                  size="large"
                                  prefix="$"
                                  style={{ width: "100%" }}
                                  min={0}
                                  formatter={(value) =>
                                    `${value}`.replace(
                                      /\B(?=(\d{3})+(?!\d))/g,
                                      ","
                                    )
                                  }
                                  parser={(value) =>
                                    value.replace(/\$\s?|(,*)/g, "")
                                  }
                                />
                              </Form.Item>
                            </FormRow>

                            <Form.Item
                              label="Additional Mandatory Fees (FTC)"
                              tooltip="Monthly fees included in total rent (e.g. trash valet, tech fee). Shown itemized for transparency."
                            >
                              <Form.List name="additional_mandatory_fees">
                                {(fields, { add, remove }) => (
                                  <>
                                    {fields.map(
                                      ({ key, name, ...restField }) => (
                                        <div
                                          key={key}
                                          style={{
                                            display: "flex",
                                            gap: 8,
                                            marginBottom: 8,
                                            alignItems: "center",
                                          }}
                                        >
                                          <Form.Item
                                            {...restField}
                                            name={[name, "name"]}
                                            rules={[
                                              {
                                                required: true,
                                                message: "Name",
                                              },
                                            ]}
                                            style={{ flex: 1, marginBottom: 0 }}
                                          >
                                            <Input placeholder="e.g. Trash Valet" />
                                          </Form.Item>
                                          <Form.Item
                                            {...restField}
                                            name={[name, "amount"]}
                                            rules={[
                                              {
                                                required: true,
                                                message: "Amount",
                                              },
                                            ]}
                                            style={{
                                              width: 100,
                                              marginBottom: 0,
                                            }}
                                          >
                                            <InputNumber
                                              prefix="$"
                                              min={0}
                                              placeholder="25"
                                            />
                                          </Form.Item>
                                          <Form.Item
                                            {...restField}
                                            name={[name, "frequency"]}
                                            initialValue="monthly"
                                            style={{
                                              width: 110,
                                              marginBottom: 0,
                                            }}
                                          >
                                            <Select
                                              size="small"
                                              options={[
                                                {
                                                  value: "monthly",
                                                  label: "Monthly",
                                                },
                                                {
                                                  value: "weekly",
                                                  label: "Weekly",
                                                },
                                              ]}
                                            />
                                          </Form.Item>
                                          <Button
                                            type="text"
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={() => remove(name)}
                                          />
                                        </div>
                                      )
                                    )}
                                    <Button
                                      type="dashed"
                                      onClick={() => add()}
                                      icon={<PlusOutlined />}
                                      block
                                    >
                                      Add mandatory fee
                                    </Button>
                                  </>
                                )}
                              </Form.List>
                            </Form.Item>
                          </>
                        )}

                        {isRentToOwn && (
                          <>
                            <FormRow className="rent-deposit-row">
                              <Form.Item
                                name={["rentToOwn", "downPayment"]}
                                label="Down Payment"
                                rules={[
                                  { required: true, message: "Required" },
                                ]}
                              >
                                <InputNumber
                                  size="large"
                                  prefix="$"
                                  style={{ width: "100%" }}
                                  min={0}
                                  formatter={(value) =>
                                    `${value}`.replace(
                                      /\B(?=(\d{3})+(?!\d))/g,
                                      ","
                                    )
                                  }
                                  parser={(value) =>
                                    value.replace(/\$\s?|(,*)/g, "")
                                  }
                                />
                              </Form.Item>

                              <Form.Item
                                name={["rentToOwn", "monthlyPayment"]}
                                label="Monthly Payment"
                                rules={[
                                  { required: true, message: "Required" },
                                ]}
                                tooltip="Monthly payment amount (taxes & insurance may not be included)"
                              >
                                <InputNumber
                                  size="large"
                                  prefix="$"
                                  style={{ width: "100%" }}
                                  min={0}
                                  formatter={(value) =>
                                    `${value}`.replace(
                                      /\B(?=(\d{3})+(?!\d))/g,
                                      ","
                                    )
                                  }
                                  parser={(value) =>
                                    value.replace(/\$\s?|(,*)/g, "")
                                  }
                                />
                              </Form.Item>
                            </FormRow>

                            <FormRow>
                              <Form.Item
                                name={["rentToOwn", "term"]}
                                label="Term (Years)"
                                rules={[
                                  { required: true, message: "Required" },
                                ]}
                              >
                                <InputNumber
                                  size="large"
                                  style={{ width: "100%" }}
                                  min={1}
                                  max={50}
                                />
                              </Form.Item>

                              <Form.Item
                                name={["rentToOwn", "interestRate"]}
                                label="Interest Rate (%)"
                                rules={[
                                  { required: true, message: "Required" },
                                ]}
                              >
                                <InputNumber
                                  size="large"
                                  style={{ width: "100%" }}
                                  min={0}
                                  max={100}
                                  step={0.1}
                                  formatter={(value) => `${value}%`}
                                  parser={(value) => value.replace("%", "")}
                                />
                              </Form.Item>
                            </FormRow>

                            <Form.Item
                              name={["rentToOwn", "creditCheckRequired"]}
                              valuePropName="checked"
                              label="Credit Check Required"
                              tooltip="If unchecked, qualification will be based on income only"
                              initialValue={false}
                              style={{ marginTop: "1.5rem" }}
                            >
                              <Switch />
                            </Form.Item>

                            <Form.Item
                              name={["rentToOwn", "taxesInsuranceIncluded"]}
                              valuePropName="checked"
                              label="Taxes & Insurance Included in Monthly Payment"
                              initialValue={false}
                            >
                              <Switch />
                            </Form.Item>

                            <Form.Item
                              name={["rentToOwn", "toursAvailable"]}
                              valuePropName="checked"
                              label="Tours Available Before Completion"
                              initialValue={false}
                            >
                              <Switch />
                            </Form.Item>

                            <Form.Item
                              name={["rentToOwn", "moveInDate"]}
                              label="Move-In Date"
                            >
                              <DatePicker
                                size="large"
                                style={{ width: "100%" }}
                                format="MMM D, YYYY"
                              />
                            </Form.Item>

                            <Form.Item
                              name={["rentToOwn", "financingDescription"]}
                              label="Financing Description"
                              tooltip="Short description shown prominently on listing (e.g., 'Owner Financing Available - No Bank Needed')"
                            >
                              <Input
                                size="large"
                                placeholder="e.g., Owner Financing Available - No Bank Needed"
                                maxLength={150}
                              />
                            </Form.Item>

                            {/* Still need rentAmount for fallback/display */}
                            <Form.Item name="rentAmount" hidden>
                              <InputNumber />
                            </Form.Item>
                            <Form.Item name="deposit" hidden initialValue={0}>
                              <InputNumber />
                            </Form.Item>
                          </>
                        )}
                      </>
                    );
                  }}
                </Form.Item>

                <FormRow>
                  <Form.Item
                    name="bedrooms"
                    label="Bedrooms"
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <InputNumber
                      size="large"
                      style={{ width: "100%" }}
                      min={0}
                    />
                  </Form.Item>

                  <Form.Item
                    name="bathrooms"
                    label="Bathrooms"
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <InputNumber
                      size="large"
                      style={{ width: "100%" }}
                      min={0}
                      step={0.5}
                    />
                  </Form.Item>
                </FormRow>

                <Form.Item
                  name="sqft"
                  label="Square Feet"
                  className="sqft-field"
                >
                  <InputNumber
                    size="large"
                    style={{ width: "100%" }}
                    min={0}
                    placeholder="Square feet"
                  />
                </Form.Item>

                <Form.Item
                  name="availableDate"
                  label="Available Date"
                  rules={[{ required: true, message: "Please select a date" }]}
                >
                  <DatePicker
                    size="large"
                    style={{ width: "100%" }}
                    format="MMM D, YYYY"
                  />
                </Form.Item>

                <Form.Item
                  name="amenities"
                  label="Amenities"
                  tooltip="Select all that apply"
                >
                  <Select
                    mode="tags"
                    size="large"
                    className="amenities-select"
                    placeholder="Add amenities (e.g., Parking, Laundry, etc.)"
                    options={[
                      { label: "Parking", value: "Parking" },
                      { label: "Laundry", value: "Laundry" },
                      { label: "Dishwasher", value: "Dishwasher" },
                      { label: "Air Conditioning", value: "Air Conditioning" },
                      { label: "Hardwood Floors", value: "Hardwood Floors" },
                      { label: "Balcony", value: "Balcony" },
                      { label: "Gym", value: "Gym" },
                      { label: "Pool", value: "Pool" },
                    ]}
                  />
                </Form.Item>

                <Form.Item
                  label="Photos"
                  tooltip="Upload photos for your listing. These will be shown to potential tenants on your listing page."
                >
                  {selectedUnit?.photos && selectedUnit.photos.length > 0 && (
                    <PhotoInfoBox>
                      <InfoCircleOutlined
                        style={{
                          color: "var(--color-primary)",
                          marginRight: "0.5rem",
                        }}
                      />
                      <span>
                        {selectedUnit.photos.length} photo(s) from this property
                        will be included automatically.
                      </span>
                    </PhotoInfoBox>
                  )}
                  {fileList.length === 0 ? (
                    <Dragger
                      multiple
                      beforeUpload={beforeUpload}
                      customRequest={handleImageUpload}
                      onRemove={handleImageRemove}
                      fileList={fileList}
                      accept="image/*"
                      disabled={isUploading}
                    >
                      <p className="ant-upload-drag-icon">
                        <UploadOutlined />
                      </p>
                      <p className="ant-upload-text">
                        Click or drag images to upload
                      </p>
                      <p className="ant-upload-hint">
                        <span className="ant-upload-hint-line1">
                          Support for multiple images.
                        </span>
                        <span className="ant-upload-hint-line2">
                          {" "}
                          Max 5MB per image.
                        </span>
                      </p>
                    </Dragger>
                  ) : (
                    <DndContext
                      sensors={dndSensors}
                      collisionDetection={closestCenter}
                      onDragStart={() => {
                        setActivePhotoUid(null);
                      }}
                      onDragEnd={({ active, over }) => {
                        if (!over || active?.id === over?.id) {
                          suppressNextTileClickRef.current = true;
                          return;
                        }
                        reorderPhotos(active.id, over.id);
                        suppressNextTileClickRef.current = true;
                      }}
                      onDragCancel={() => {
                        suppressNextTileClickRef.current = true;
                      }}
                    >
                      <SortableContext
                        items={fileList.map((f) => f.uid)}
                        strategy={rectSortingStrategy}
                      >
                        <Upload
                          listType="picture-card"
                          multiple
                          beforeUpload={beforeUpload}
                          customRequest={handleImageUpload}
                          onRemove={handleImageRemove}
                          fileList={fileList}
                          accept="image/*"
                          disabled={isUploading}
                          showUploadList={{
                            showPreviewIcon: true,
                            showRemoveIcon: true,
                            removeIcon: <CloseOutlined />,
                          }}
                          itemRender={(originNode, file) => {
                            return (
                              <SortablePhotoTile id={file.uid} file={file}>
                                {originNode}
                              </SortablePhotoTile>
                            );
                          }}
                          onPreview={seePhotoPreview}
                        >
                          <UploadAnotherTile>
                            {isUploading ? (
                              <LoadingOutlined spin />
                            ) : (
                              <PlusOutlined />
                            )}
                          </UploadAnotherTile>
                        </Upload>
                      </SortableContext>
                    </DndContext>
                  )}
                  {uploadedPhotoUrls.length > 0 && (
                    <div style={{ marginTop: "1rem" }}>
                      <p
                        style={{
                          fontSize: "0.875rem",
                          color: "var(--color-text-secondary)",
                          marginBottom: "0.5rem",
                        }}
                      >
                        {uploadedPhotoUrls.length} new{" "}
                        {pluralize("photo", uploadedPhotoUrls.length)} uploaded
                      </p>
                    </div>
                  )}
                </Form.Item>
              </SectionWrapper>

              <SectionDivider />

              {/* Requirements */}
              <SectionWrapper>
                <StepHeader>
                  <StepNumber>3</StepNumber>
                  <StepTitle>Tenant Requirements</StepTitle>
                </StepHeader>

                <RequirementsNote>
                  <InfoCircleOutlined />
                  <span>
                    These requirements will be displayed on the listing and used
                    to pre-qualify inquiries.
                  </span>
                </RequirementsNote>

                <Form.Item
                  name="minCreditScore"
                  label="Minimum Credit Score"
                  rules={[
                    {
                      validator: async (_, value) => {
                        // AntD `required: true` treats null as empty, but here null is a valid "No minimum" selection.
                        if (value === undefined) throw new Error("Required");
                      },
                    },
                  ]}
                >
                  <Select
                    size="large"
                    options={[
                      { label: "No minimum", value: null },
                      { label: "550+ (Below Average)", value: 550 },
                      { label: "600+ (Fair)", value: 600 },
                      { label: "650+ (Good)", value: 650 },
                      { label: "700+ (Very Good)", value: 700 },
                      { label: "750+ (Excellent)", value: 750 },
                    ]}
                  />
                </Form.Item>

                <Form.Item
                  name="minIncomeMultiple"
                  label="Minimum Income Requirement"
                  rules={[
                    {
                      validator: async (_, value) => {
                        // AntD `required: true` treats null as empty, but here null is a valid "No minimum" selection.
                        if (value === undefined) throw new Error("Required");
                      },
                    },
                  ]}
                  tooltip="Income must be at least X times the monthly rent"
                >
                  <Select
                    size="large"
                    options={[
                      { label: "No minimum", value: null },
                      { label: "1.5x Monthly Rent", value: 1.5 },
                      { label: "2x Monthly Rent", value: 2 },
                      { label: "2.5x Monthly Rent", value: 2.5 },
                      { label: "3x Monthly Rent", value: 3 },
                      { label: "3.5x Monthly Rent", value: 3.5 },
                      { label: "4x Monthly Rent", value: 4 },
                    ]}
                  />
                </Form.Item>

                <Form.Item
                  name="availability"
                  label={
                    <span>
                      Tour Availability{" "}
                      <Popover
                        content="Set the days and times you can do showings. Leo AI will offer these slots to qualified prospects."
                        trigger="click"
                        overlayClassName="tour-availability-popover"
                        placement="topLeft"
                      >
                        <InfoCircleOutlined
                          style={{
                            color: "var(--color-primary)",
                            cursor: "pointer",
                            marginLeft: "4px",
                          }}
                        />
                      </Popover>
                    </span>
                  }
                >
                  <AvailabilityScheduler />
                </Form.Item>

                <PetsToggleWrapper>
                  <Form.Item
                    name="petsAllowed"
                    label="Pets Allowed"
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="Yes" unCheckedChildren="No" />
                  </Form.Item>
                </PetsToggleWrapper>

                {/* Additional Screening Questions */}
                <ScreeningSectionDivider>
                  <ScreeningSectionTitle>
                    Additional Screening Questions
                  </ScreeningSectionTitle>
                  <ScreeningSectionNote>
                    Select which additional questions Leo AI should ask prospects
                    during pre-screening.
                  </ScreeningSectionNote>
                </ScreeningSectionDivider>

                {/* Living Conditions */}
                <ScreeningSubsection>
                  <ScreeningSubsectionTitle>
                    Living Conditions
                  </ScreeningSubsectionTitle>

                  <Form.Item
                    name="askSmoking"
                    label="Ask about smoking"
                    valuePropName="checked"
                    tooltip="Leo AI will ask prospects if they smoke"
                    initialValue={false}
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    name="askOccupants"
                    label="Ask about number of occupants"
                    valuePropName="checked"
                    tooltip="Leo AI will ask prospects how many people will be living in the property"
                    initialValue={false}
                  >
                    <Switch />
                  </Form.Item>
                </ScreeningSubsection>

                {/* Financial */}
                <ScreeningSubsection>
                  <ScreeningSubsectionTitle>Financial</ScreeningSubsectionTitle>

                  <Form.Item
                    name="askGuarantor"
                    label="Ask about guarantor"
                    valuePropName="checked"
                    tooltip="Leo AI will ask prospects if they have a guarantor"
                    initialValue={false}
                  >
                    <Switch />
                  </Form.Item>
                </ScreeningSubsection>

                {/* Legal */}
                <ScreeningSubsection>
                  <ScreeningSubsectionTitle>Legal</ScreeningSubsectionTitle>

                  <Form.Item
                    name="askLawsuit"
                    label="Ask about lawsuits"
                    valuePropName="checked"
                    tooltip="Leo AI will ask prospects if they have been party to a lawsuit"
                    initialValue={false}
                  >
                    <Switch />
                  </Form.Item>

                  <Form.Item
                    name="askFelony"
                    label="Ask about felony convictions"
                    valuePropName="checked"
                    tooltip="Leo AI will ask prospects if they have been convicted of a felony"
                    initialValue={false}
                  >
                    <Switch />
                  </Form.Item>
                </ScreeningSubsection>
              </SectionWrapper>

              <SectionDivider />

              {/* Leo Private Information */}
              <SectionWrapper>
                <StepHeader>
                  <StepNumber>4</StepNumber>
                  <StepTitle>Private information for Leo AI</StepTitle>
                </StepHeader>

                <PrivateInfoNote>
                  <InfoCircleOutlined />
                  <span>
                    Only Leo AI sees this info. Tell Leo AI which rules to enforce
                    while validating prospects. Tenants never see this content.
                  </span>
                </PrivateInfoNote>

                <Form.Item
                  name="leoPrivateInfo"
                  label="Additional information for Leo AI"
                  tooltip='Share any private validation rules for Leo AI (tenants never see this). Example: "No smoking," "Previous evictions disqualify," "Require references," or "Prefer long-term stays."'
                  extra={
                    <Form.Item
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.leoPrivateInfo !==
                        currentValues.leoPrivateInfo
                      }
                      noStyle
                    >
                      {({ getFieldValue }) => (
                        <CharacterCounter>
                          {getFieldValue("leoPrivateInfo")?.length || 0} / 2000
                        </CharacterCounter>
                      )}
                    </Form.Item>
                  }
                >
                  <TextArea
                    rows={6}
                    placeholder="Example: No smoking allowed. Prefer tenants with stable employment history. Must provide references from previous landlords. Previous evictions are disqualifying. Prefer tenants who plan to stay long-term (2+ years)."
                    maxLength={2000}
                  />
                </Form.Item>
              </SectionWrapper>

              <SectionDivider />

              {/* Leo Pre-Screening Info */}
              <LeoInfoBox>
                <LeoInfoIcon>
                  <img
                    src="/icons/leo/leo.png"
                    alt="Leo AI"
                    style={{ width: "24px", height: "24px" }}
                  />
                </LeoInfoIcon>
                <LeoInfoContent>
                  <LeoInfoTitle>
                    Let Leo AI pre-screen prospective tenants for you
                  </LeoInfoTitle>
                  <LeoInfoDescription>
                    When prospects visit your listing link, Leo AI (your AI
                    assistant) will ask them about their credit score, income,
                    and pets before they can contact you. Only qualified
                    prospects who meet your requirements will be able to send
                    you an inquiry.
                  </LeoInfoDescription>
                </LeoInfoContent>
              </LeoInfoBox>

              {/* Submit */}
              <FormActions>
                <Button size="large" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  size="large"
                  htmlType="submit"
                  loading={createMutation.isLoading}
                  icon={
                    <CheckCircleOutlined
                      style={{ filter: "brightness(0) invert(1)" }}
                    />
                  }
                >
                  {isEditing ? "Update Listing" : "Create Listing"}
                </Button>
              </FormActions>
            </>
          )}
        </StyledForm>
      </StyledModal>
    </>
  );
};

// Styled Components
const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
    padding: 0;
  }

  .ant-modal-header {
    background: linear-gradient(
      135deg,
      var(--color-primary-darker) 0%,
      var(--color-primary-dark) 100%
    );
    border-bottom: none;
    border-radius: 16px 16px 0 0;
    padding: 1.75rem 2rem;
    margin: 0;
  }

  .ant-modal-title {
    color: white !important;
    font-family: var(--font-primary);
    font-weight: 700;
    font-size: 1.5rem;
    line-height: 1.2;
    letter-spacing: -0.02em;
    text-align: center;
  }

  .ant-modal-close {
    color: white !important;
    font-size: 18px;
    top: 18px;
    right: 24px;
    background: transparent !important;
    border-radius: 0 !important;
    width: 36px !important;
    height: 36px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border: none !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;

    .anticon,
    .anticon svg,
    svg {
      color: white !important;
      fill: white !important;
    }

    &:hover {
      color: white !important;
      background: transparent !important;
      border-color: transparent !important;
    }

    &:hover .anticon,
    &:hover .anticon svg,
    &:hover svg {
      color: white !important;
      fill: white !important;
    }
  }

  .ant-modal-body {
    padding: 2rem;
    background: #f8fafc;
    overflow-x: hidden;
    max-height: 70vh;
    overflow-y: auto;
    /* Reserve space for the scrollbar so it does not overlay full-width controls (e.g. property Select) */
    scrollbar-gutter: stable;

    /* Custom scrollbar */
    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #e2e8f0;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #94a3b8;
      border-radius: 4px;

      &:hover {
        background: var(--color-primary-light);
      }
    }
  }
`;

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 1.5rem;
  }

  .ant-form-item.sqft-field {
    margin-top: 1.5rem;
  }

  .ant-form-item-label > label {
    font-family: var(--font-primary);
    font-weight: 600;
    font-size: 0.95rem;
    color: var(--color-text-primary);
    height: auto;
    margin-bottom: 0.5rem;
  }

  .ant-form-item-extra {
    font-size: 0.875rem;
    color: var(--color-text-secondary);
    margin-top: 0.25rem;
  }

  /* Base input styling - cleaner borders */
  .ant-input,
  .ant-picker,
  .ant-input-textarea {
    border-radius: 8px !important;
    border: 1px solid #e2e8f0 !important;
    background: white !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04) !important;

    &:hover:not(:disabled) {
      border-color: var(--color-primary-light) !important;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06) !important;
    }
  }

  /* InputNumber - no border change on hover */
  .ant-input-number {
    border-radius: 8px !important;
    border: 1px solid #e2e8f0 !important;
    background: white !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04) !important;

    &:hover:not(:disabled):not(.ant-input-number-focused) {
      border-color: #e2e8f0 !important;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04) !important;
    }
  }

  /* TextArea specific - remove inner borders */
  .ant-input-textarea textarea {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;

    &:focus {
      border: none !important;
      box-shadow: none !important;
      outline: none !important;
    }
  }

  /* Upload (Dragger) - tighter + shorter */
  .ant-upload-wrapper {
    width: 100%;
  }

  .ant-upload.ant-upload-drag {
    padding: 0.5rem 0.75rem !important;
    min-height: 60px !important;
    border-radius: 12px !important;
    background: #ffffff !important;
    max-height: 150px;
  }

  .ant-upload-drag-container {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
  }

  .ant-upload-drag-icon {
    margin-bottom: 0.15rem !important;

    svg {
      font-size: 2rem !important;
    }
  }

  .ant-upload-text {
    font-size: 0.9rem !important;
    line-height: 1.3 !important;
    color: var(--color-text) !important;
    margin: 0.05rem 0 !important;
    white-space: nowrap !important;
  }

  .ant-upload-hint {
    font-size: 0.75rem !important;
    line-height: 1.2 !important;
    color: var(--color-text-secondary) !important;
    margin: 0.05rem 0 0 !important;

    .ant-upload-hint-line2 {
      display: inline;
    }
  }

  @media (max-width: 640px) {
    .ant-upload.ant-upload-drag {
      padding: 0.4rem 0.6rem !important;
      min-height: 55px !important;
    }

    .ant-upload-drag-icon svg {
      font-size: 1.75rem !important;
    }

    .ant-upload-text {
      font-size: 0.85rem !important;
    }

    .ant-upload-hint {
      font-size: 0.7rem !important;

      .ant-upload-hint-line2 {
        display: block;
        margin-top: 0.25rem;
      }
    }
  }

  /* Select specific styling */
  .ant-select {
    .ant-select-selector {
      border-radius: 8px !important;
      border: 1px solid #e2e8f0 !important;
      background: white !important;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04) !important;
      padding: 0 11px !important;
    }

    &:hover .ant-select-selector {
      border-color: var(--color-primary-light) !important;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06) !important;
    }

    &.ant-select-focused .ant-select-selector {
      border-color: var(--color-primary) !important;
      box-shadow:
        0 0 0 3px var(--color-primary-glow),
        0 2px 4px rgba(0, 0, 0, 0.06) !important;
      outline: none !important;
    }
  }

  /* Remove any accidental "pill/tag" styling from single Select values */
  .ant-select-single .ant-select-selector .ant-select-selection-item {
    background: transparent !important;
    border: none !important;
    border-radius: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
    box-shadow: none !important;
  }

  /* Focus states */
  .ant-input:focus,
  .ant-input-focused,
  .ant-picker-focused,
  .ant-input-textarea-focused {
    border-color: var(--color-primary) !important;
    box-shadow:
      0 0 0 3px var(--color-primary-glow),
      0 2px 4px rgba(0, 0, 0, 0.06) !important;
    outline: none !important;
  }

  .ant-picker-cell,
  .ant-picker-cell-inner {
    border: none !important;
    box-shadow: none !important;
  }

  .ant-picker-cell-selected,
  .ant-picker-cell-selected .ant-picker-cell-inner {
    background: transparent !important;
  }

  .ant-picker-cell-selected .ant-picker-cell-inner {
    background: var(--color-primary-dark) !important;
    color: white !important;
    border-radius: 6px;
    border: none;
  }

  /* InputNumber - no border/outline change on focus */
  .ant-input-number:focus,
  .ant-input-number-focused,
  .ant-input-number.ant-input-number-focused {
    border-color: #e2e8f0 !important;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04) !important;
    outline: none !important;
  }

  .rent-deposit-row .ant-input-number,
  .rent-deposit-row .ant-input-number-input-wrap,
  .rent-deposit-row .ant-input-number-input {
    border-color: #e2e8f0 !important;
    box-shadow: none !important;
  }

  .rent-deposit-row .ant-input-number-focused .ant-input-number-input-wrap,
  .rent-deposit-row .ant-input-number-input:focus {
    border-color: #e2e8f0 !important;
    box-shadow: none !important;
  }

  /* Remove inner borders and shadows from nested inputs */
  .ant-input-number-input,
  .ant-select-selection-search-input {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
  }

  /* Remove all inner borders from InputNumber */
  .ant-input-number {
    .ant-input-number-input-wrap {
      border: none !important;
      box-shadow: none !important;
    }

    .ant-input-number-input {
      border: none !important;
      box-shadow: none !important;
      outline: none !important;
      background: transparent !important;
    }

    .ant-input-number-handler-wrap {
      border: none !important;
    }

    &:hover .ant-input-number-input {
      border: none !important;
      box-shadow: none !important;
    }

    &.ant-input-number-focused .ant-input-number-input,
    &.ant-input-number-focused .ant-input-number-input-wrap,
    &:focus .ant-input-number-input,
    &:focus .ant-input-number-input-wrap {
      border: none !important;
      box-shadow: none !important;
      outline: none !important;
    }

    /* Remove borders from prefix/suffix addons */
    .ant-input-number-prefix,
    .ant-input-number-suffix {
      border: none !important;
      background: transparent !important;
    }
  }

  /* Remove inner styling from Select internals */
  .ant-select-selection-search {
    background: transparent !important;
  }

  .ant-select-selection-placeholder {
    background: transparent !important;
    border: none !important;
    line-height: 32px !important;
  }

  /* Ensure full width for InputNumber */
  .ant-input-number {
    width: 100%;
  }

  /* Amenities only: pill-style tags (avoid affecting other Select dropdowns) */
  .amenities-select.ant-select {
    /* Add a bit of top padding so selected chips don't hug the top edge */
    .ant-select-selector {
      padding-top: 6px !important;
      padding-bottom: 6px !important;
    }

    .ant-select-selection-overflow {
      gap: 8px;
    }

    .ant-select-selection-overflow-item {
      margin: 0 !important;
    }

    /* Tag chip */
    .ant-select-selection-item {
      display: inline-flex !important;
      align-items: center !important;
      height: auto !important; /* prevent letter clipping */
      min-height: 28px !important; /* consistent pill height */
      line-height: 1.2 !important; /* readable and no clipping */
      padding: 4px 10px !important; /* vertical padding so glyphs never clip */
      border-radius: 999px !important;

      background: rgba(44, 95, 45, 0.1) !important;
      border: 1px solid rgba(44, 95, 45, 0.22) !important;
      color: var(--color-primary-dark) !important;

      font-size: 0.86rem !important;
      font-weight: 600 !important;
      letter-spacing: -0.01em;
    }

    .ant-select-selection-item-content {
      line-height: 1.2 !important;
      padding: 0 !important;
    }

    .ant-select-selection-item-remove {
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      width: 18px !important;
      height: 18px !important;
      margin-left: 8px !important;
      border-radius: 999px !important;

      color: rgba(44, 95, 45, 0.85) !important;
      background: transparent !important;

      svg {
        width: 12px !important;
        height: 12px !important;
      }

      &:hover {
        color: var(--color-primary-dark) !important;
        background: rgba(44, 95, 45, 0.12) !important;
      }
    }
  }

  @media (max-width: 640px) {
    .amenities-select.ant-select .ant-select-selection-item {
      min-height: 26px !important;
      font-size: 0.82rem !important;
      padding: 3px 9px !important;
    }
  }

  /* Remove extra borders from InputNumber wrapper and group */
  .ant-input-number-group-wrapper {
    box-shadow: none !important;
    border: none !important;

    .ant-input-number-group-addon {
      border: none !important;
      background: transparent !important;
      box-shadow: none !important;
    }

    .ant-input-number {
      border: none !important;
    }
  }

  /* Remove borders from InputNumber with prefix/suffix */
  .ant-input-number-group {
    border: none !important;
    box-shadow: none !important;

    .ant-input-number-group-addon {
      border: none !important;
      background: transparent !important;
      box-shadow: none !important;
    }

    .ant-input-number {
      border: none !important;
      box-shadow: none !important;
    }
  }

  /* Remove inner input border when using prefix */
  .ant-input-number-group .ant-input-number-input-wrap,
  .ant-input-number-group-wrapper .ant-input-number-input-wrap {
    border: none !important;
    box-shadow: none !important;
  }

  .ant-input-number-group .ant-input-number-input,
  .ant-input-number-group-wrapper .ant-input-number-input {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
  }

  /* Upload thumbnails grid */
  .ant-upload-list-picture-card-container,
  .ant-upload.ant-upload-select-picture-card {
    width: 108px !important;
    height: 108px !important;
  }

  .ant-upload-list-picture-card-container {
    border-radius: 12px !important;
    overflow: hidden;
    padding: 0 !important;
    margin: 0 !important;
  }

  .ant-upload-list-picture-card .ant-upload-list-item {
    border-radius: 12px !important;
    overflow: hidden !important;
    height: 100% !important;
    position: relative !important;
    width: 100% !important;
    padding: 0 !important; /* remove Ant default inner padding so image fills the tile */
    margin: 0 !important;
    display: block !important;
  }

  /* Our drag wrapper should also fill the square */
  .photo-dnd-item {
    width: 100%;
    height: 100%;
    display: block;
    position: relative; /* anchor the drag handle inside each tile */
    /* Allow normal scrolling in the modal; we disable touch-action only while actively dragging */
    touch-action: pan-y;
  }

  /* Drag visual feedback (dnd-kit) */
  .photo-dnd-item.is-dnd-dragging {
    position: relative;
    z-index: 5;
    touch-action: none;
  }

  /* Let elementFromPoint "see through" the dragged tile to detect drop targets */
  .photo-dnd-item.is-dnd-dragging .ant-upload-list-item {
    pointer-events: none;
  }

  /* Drag handle (standard mobile UX) */
  .photo-drag-handle {
    position: absolute;
    top: 6px;
    left: 6px;
    width: 28px;
    height: 28px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.45);
    color: #fff;
    z-index: 6;
    pointer-events: auto;
    cursor: grab;
    user-select: none;
    touch-action: none; /* critical: ensures touch drag starts instead of scrolling */
  }

  .photo-drag-handle .anticon,
  .photo-drag-handle svg {
    color: #ffffff !important;
    fill: #ffffff !important;
    stroke: #ffffff !important;
  }

  .photo-drag-handle:active {
    cursor: grabbing;
  }

  .photo-drag-handle .anticon {
    font-size: 16px;
    line-height: 1;
  }

  @media (hover: hover) and (pointer: fine) {
    .photo-drag-handle {
      opacity: 0;
      transition: opacity 0.15s ease;
    }
    .photo-dnd-item:hover .photo-drag-handle {
      opacity: 1;
    }
  }

  .photo-dnd-item.is-dnd-dragging .ant-upload-list-item {
    transform: scale(1.04);
    box-shadow: 0 10px 22px rgba(0, 0, 0, 0.22) !important;
    outline: 2px solid rgba(34, 197, 94, 0.9); /* primary-ish green */
    outline-offset: -2px;
    filter: saturate(1.05) contrast(1.02);
  }

  /* Note: dnd-kit doesn't apply an "over" class by default; keep UX focused on the dragged tile + handle */

  /* Make hover/preview overlay respect rounded corners and cover full tile */
  .ant-upload-list-picture-card .ant-upload-list-item::before {
    /* Don't rely on Ant's pseudo-overlay (varies by antd version/theme). */
    display: none !important;
  }

  .ant-upload-list-picture-card .ant-upload-list-item-actions {
    border-radius: 12px !important;
    inset: 0 !important;
    position: absolute !important;
    top: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    left: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 14px !important;
    margin: 0 !important;
    padding: 0 !important;
    pointer-events: none; /* let the inner buttons handle events */
    opacity: 0 !important;
    transition: opacity 0.15s ease !important;
    z-index: 2 !important;
    /* Use the actions layer itself as the full-tile overlay background */
    background: rgba(0, 0, 0, 0.38) !important;
  }

  /* Trigger on the container hover (more reliable than relying on the pseudo element hover target) */
  .ant-upload-list-picture-card-container:hover .ant-upload-list-item-actions,
  .ant-upload-list-picture-card
    .ant-upload-list-item:hover
    .ant-upload-list-item-actions {
    opacity: 1 !important;
  }

  /* Mobile tap-to-reveal: show overlay controls for the active tile */
  .photo-dnd-item.is-active .ant-upload-list-item-actions {
    opacity: 1 !important;
  }

  /* AntD can animate the individual action buttons separately; keep them fully visible when active */
  .photo-dnd-item.is-active .ant-upload-list-item-actions > *,
  .photo-dnd-item.is-active .ant-upload-list-item-actions a,
  .photo-dnd-item.is-active .ant-upload-list-item-actions button {
    opacity: 1 !important;
    visibility: visible !important;
  }

  /* On touch devices, disable action fade transitions to avoid "flash away" behavior */
  @media (hover: none) and (pointer: coarse) {
    .ant-upload-list-picture-card .ant-upload-list-item-actions,
    .ant-upload-list-picture-card .ant-upload-list-item-actions a,
    .ant-upload-list-picture-card .ant-upload-list-item-actions button,
    .ant-upload-list-picture-card .ant-upload-list-item-actions .anticon,
    .ant-upload-list-picture-card .ant-upload-list-item-actions svg {
      transition: none !important;
    }
  }

  /* Ensure the clickable action buttons still receive pointer events */
  .ant-upload-list-picture-card .ant-upload-list-item-actions > * {
    pointer-events: auto;
  }

  /* Make action icons white */
  .ant-upload-list-picture-card .ant-upload-list-item-actions .anticon,
  .ant-upload-list-picture-card .ant-upload-list-item-actions svg {
    color: #ffffff !important;
    fill: #ffffff !important;
    stroke: #ffffff !important;
  }

  /* Ensure action icons never get a background "pill" on hover (especially the X/remove icon) */
  .ant-upload-list-picture-card .ant-upload-list-item-actions a,
  .ant-upload-list-picture-card .ant-upload-list-item-actions button {
    background: transparent !important;
    box-shadow: none !important;
    border: none !important;
  }

  .ant-upload-list-picture-card .ant-upload-list-item-actions a:hover,
  .ant-upload-list-picture-card .ant-upload-list-item-actions button:hover {
    background: transparent !important;
    box-shadow: none !important;
  }

  /* While dragging to reorder, hide the overlay on desktop hover devices only.
     On touch devices, hiding causes the X to "flash away" when a tap is interpreted as a drag. */
  @media (hover: hover) and (pointer: fine) {
    .photo-dnd-item.is-dnd-dragging .ant-upload-list-item-actions {
      opacity: 0 !important;
    }
  }

  .ant-upload-list-picture-card .ant-upload-list-item-thumbnail,
  .ant-upload-list-picture-card .ant-upload-list-item-thumbnail img {
    border-radius: 12px !important;
  }

  /* Ensure uploaded images fill the full square (so overlay covers the whole tile) */
  .ant-upload-list-picture-card .ant-upload-list-item-thumbnail {
    width: 100% !important;
    height: 100% !important;
    display: block !important;
    padding: 0 !important;
    margin: 0 !important;
    position: relative !important;
    z-index: 0 !important;
  }

  .ant-upload-list-picture-card .ant-upload-list-item-thumbnail a {
    display: block !important;
    width: 100% !important;
    height: 100% !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  .ant-upload-list-picture-card .ant-upload-list-item-thumbnail picture {
    display: block !important;
    width: 100% !important;
    height: 100% !important;
  }

  .ant-upload-list-picture-card .ant-upload-list-item-thumbnail img {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    object-position: center !important;
    display: block !important;
  }

  @media (max-width: 640px) {
    .ant-upload-list-picture-card-container,
    .ant-upload.ant-upload-select-picture-card {
      width: 96px !important;
      height: 96px !important;
    }
  }
`;

const UploadAnotherTile = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  color: var(--color-text-secondary);
  font-family: var(--font-secondary);
  font-weight: 600;
  line-height: 1.15;
  text-align: center;

  .anticon {
    font-size: 1.25rem;
    color: var(--color-primary);
  }

  span {
    font-size: 0.85rem;
  }
`;

const SectionWrapper = styled.div`
  position: relative;
  z-index: 2;
  margin-bottom: 0;
  background: white;
  padding: 2rem;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  @media (max-width: 640px) {
    padding: 1.5rem;
    border-radius: 8px;
  }
`;

const SectionDivider = styled(Divider)`
  margin: 2rem 0 !important;
  border-top: none !important;
  border-bottom: none !important;
  opacity: 0;
`;

const ModalTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  font-size: 1.35rem;
  font-weight: 700;
  color: white;

  .anticon {
    font-size: 1.5rem;
  }
`;

const StepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.75rem;
`;

const StepNumber = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(
    135deg,
    var(--color-primary) 0%,
    var(--color-primary-dark) 100%
  );
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  box-shadow: 0 2px 8px rgba(44, 95, 45, 0.2);
  flex-shrink: 0;
`;

const StepTitle = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-text-primary);
  font-family: var(--font-primary);
  letter-spacing: -0.01em;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;

  .ant-form-item {
    margin-bottom: 0;
  }

  &.rent-deposit-row {
    margin-bottom: 1.5rem;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;

    .ant-form-item {
      margin-bottom: 0;
    }

    &.rent-deposit-row {
      margin-bottom: 1.5rem;
    }
  }
`;

const StyledRadioGroup = styled(Radio.Group)`
  .ant-radio-button-wrapper {
    &:first-child {
      border-left: 1px solid #d9d9d9;
      border-radius: 6px 0 0 6px;
    }

    &:last-child {
      border-left: 1px solid #d9d9d9;
      border-radius: 0 6px 6px 0;
    }

    &.ant-radio-button-wrapper-checked {
      border-left: 1px solid var(--color-primary);

      &:first-child {
        border-left: 1px solid var(--color-primary);
      }

      &:last-child {
        border-left: 1px solid var(--color-primary);
      }
    }
  }
`;

const PetsToggleWrapper = styled.div`
  margin-top: 0.5rem;
  padding: 0.875rem 1rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  &:hover {
    border-color: var(--color-primary-light);
    background: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  }

  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-form-item-label {
    padding-bottom: 0.375rem;

    > label {
      font-size: 0.95rem !important;
      font-weight: 600 !important;
      color: var(--color-text-primary) !important;
    }
  }

  .ant-switch {
    background-color: #cbd5e1;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

    .ant-switch-inner {
      line-height: 22px;
      vertical-align: middle;
    }

    &.ant-switch-checked {
      background-color: var(--color-success);
    }

    &:focus {
      box-shadow: 0 0 0 3px var(--color-success-glow);
    }
  }
`;

const PhotoInfoBox = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background: var(--color-primary-glow);
  border: 1px solid var(--color-primary);
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: var(--color-text);

  .anticon {
    flex-shrink: 0;
  }
`;

const LeoInfoBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #7dd3fc;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px rgba(14, 165, 233, 0.1);
`;

const LeoInfoIcon = styled.div`
  .anticon {
    color: #0ea5e9;
    font-size: 2rem;
    margin: 0 auto;
    display: block;
    text-align: center;
  }

  img {
    /* Recolor leo.png to match the header color (#0c4a6e) */
    filter: brightness(0) saturate(100%) invert(18%) sepia(24%) saturate(1450%)
      hue-rotate(166deg) brightness(92%) contrast(92%);
  }
`;

const LeoInfoContent = styled.div`
  width: 100%;
`;

const LeoInfoTitle = styled.h4`
  font-family: var(--font-primary);
  font-size: 1.125rem;
  font-weight: 700;
  color: #0c4a6e;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const LeoInfoDescription = styled.p`
  font-family: var(--font-secondary);
  font-size: 0.9375rem;
  color: #075985;
  line-height: 1.6;
  margin: 0;
  text-align: center;
`;

const CharacterCounter = styled.div`
  display: block;
  text-align: right;
  font-size: 0.875rem;
  color: var(--color-text-tertiary);
  font-family: var(--font-secondary);
  line-height: 1.5;
  width: 100%;
`;

const FormActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e2e8f0;

  .ant-btn {
    min-width: 140px;
    height: 44px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 1rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &.ant-btn-primary {
      background: linear-gradient(
        135deg,
        var(--color-primary) 0%,
        var(--color-primary-dark) 100%
      );
      box-shadow: 0 2px 8px rgba(44, 95, 45, 0.2);
      color: white;
      border: none;

      .anticon {
        color: white !important;
      }

      &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 4px 16px rgba(44, 95, 45, 0.3);
        opacity: 0.95;
      }

      &:active {
        transform: translateY(0);
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }

    &:not(.ant-btn-primary) {
      background: white;
      border: 1px solid #e2e8f0;
      color: #475569;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

      &:hover:not(:disabled) {
        border-color: var(--color-primary);
        color: var(--color-primary);
        background: #f8fafc;
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }

  @media (max-width: 640px) {
    flex-direction: column-reverse;

    .ant-btn {
      width: 100%;
    }
  }
`;

const RequirementsNote = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.75rem;
  padding: 1.25rem;
  background: #eff6ff;
  border: 1px solid #93c5fd;
  border-radius: 12px;
  font-size: 0.9rem;
  color: #1e3a8a;
  margin-bottom: 1.75rem;
  line-height: 1.6;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  .anticon {
    color: #3b82f6;
    font-size: 1.5rem;
    margin: 0 auto;
    display: block;
    text-align: center;
  }

  span {
    width: 100%;
    text-align: center;
  }
`;

const ScreeningSectionDivider = styled.div`
  margin: 2rem 0 1.5rem 0;
  padding-top: 2rem;
  border-top: 1px solid #e2e8f0;
`;

const ScreeningSectionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--color-text-primary);
  font-family: var(--font-primary);
  margin-bottom: 0.5rem;
  letter-spacing: -0.01em;
`;

const ScreeningSectionNote = styled.p`
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  margin: 0 0 1.5rem 0;
  line-height: 1.5;
`;

const ScreeningSubsection = styled.div`
  margin-bottom: 1.5rem;
  padding: 1.25rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;

  .ant-form-item {
    margin-bottom: 1.25rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const ScreeningSubsectionTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  font-family: var(--font-primary);
  margin-bottom: 1rem;
  letter-spacing: -0.01em;
`;

const PrivateInfoNote = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  padding: 0.85rem 1rem;
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  font-size: 17px !important;
  color: #92400e;
  margin-bottom: 1.5rem;
  line-height: 1.35;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  .anticon {
    color: #f59e0b;
    font-size: 1.4rem;
    margin: 0;
    display: block;
  }

  span {
    width: 100%;
    text-align: center;
    font-size: 17px;
  }
`;

const EmptyUnitsAlert = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem;
  background: #fffbeb;
  border: 1px solid #fbbf24;
  border-radius: 12px;
  font-size: 0.9rem;
  color: #92400e;
  margin-top: 1rem;
  line-height: 1.6;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  .anticon {
    font-size: 1.25rem;
    margin-top: 0.125rem;
    flex-shrink: 0;
    color: #f59e0b;
  }

  span {
    flex: 1;
  }
`;

const EmptyStateContainer = styled.div`
  position: relative;
  z-index: 0;
  margin-top: 2.5rem;
  padding: 3rem 2rem;
  background: white;
  border: 2px dashed #cbd5e1;
  border-radius: 16px;
  text-align: center;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  /* Decorative block must not sit above the property Select in the stacking order or steal clicks */
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }

  @media (max-width: 640px) {
    padding: 2rem 1.5rem;
    margin-top: 2rem;
  }
`;

const EmptyStateIcon = styled.div`
  margin-bottom: 1rem;

  .anticon {
    font-size: 4rem;
    color: var(--color-primary);
    filter: drop-shadow(0 4px 12px rgba(59, 130, 246, 0.3));
  }

  @media (max-width: 640px) {
    .anticon {
      font-size: 3rem;
    }
  }
`;

const EmptyStateTitle = styled.h3`
  font-family: var(--font-primary);
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: 0.75rem;
  letter-spacing: -0.01em;

  @media (max-width: 640px) {
    font-size: 1.25rem;
  }
`;

const EmptyStateDescription = styled.p`
  font-family: var(--font-secondary);
  font-size: 1rem;
  color: var(--color-text-secondary);
  line-height: 1.6;
  max-width: 480px;
  margin: 0 auto 2rem;

  @media (max-width: 640px) {
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
  }
`;

const StepsPreview = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 420px;
  margin: 0 auto;
`;

const StepPreviewItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: white;
  border: 1px solid var(--color-border-light);
  border-radius: 10px;
  text-align: left;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);

  &:hover {
    border-color: var(--color-primary-light);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
    transform: translateX(4px);
    background: var(--color-primary-glow);
  }

  @media (max-width: 640px) {
    padding: 0.875rem 1rem;
    gap: 0.75rem;
  }
`;

const StepPreviewNumber = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(
    135deg,
    var(--color-primary-light) 0%,
    var(--color-primary) 100%
  );
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);

  @media (max-width: 640px) {
    width: 28px;
    height: 28px;
    font-size: 0.8rem;
  }
`;

const StepPreviewText = styled.span`
  font-family: var(--font-secondary);
  font-size: 0.95rem;
  color: var(--color-text-primary);
  font-weight: 500;

  @media (max-width: 640px) {
    font-size: 0.875rem;
  }
`;

export default CreateListingModal;
